import { LitElement, html } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { Task } from "@lit/task";
import { fetchApi, types, imageCache } from "../api/index.js";
import { navigate, getSearchParam, setSearchParam } from "../router.js";
import { uniqueCanonicals } from "../utils/canonical.js";
import "../components/loading-spinner.js";
import "../components/error-message.js";
import "../components/copy-button.js";
import "../tabs/tags-tab.js";
import "../tabs/overview-tab.js";
import "../tabs/details-tab.js";
import "../tabs/sbom-tab.js";
import "../tabs/history-tab.js";
import "../tabs/vulnerabilities-tab.js";
import "../tabs/comparison-tab.js";

const VULNERABILITIES_ENABLED = import.meta.env.VITE_VULNERABILITIES_ENABLED === "true";

type TabId = "tags" | "overview" | "details" | "sbom" | "history" | "vulnerabilities" | "comparison";

const ALL_TABS: Array<{ id: TabId; label: string; hidden?: boolean }> = [
  { id: "tags", label: "Tags" },
  { id: "overview", label: "Overview" },
  { id: "details", label: "Details" },
  { id: "sbom", label: "SBOM" },
  { id: "history", label: "History" },
  { id: "vulnerabilities", label: "Vulnerabilities", hidden: !VULNERABILITIES_ENABLED },
  { id: "comparison", label: "Comparison" },
];

@customElement("image-detail")
export class ImageDetail extends LitElement {
  @property() imageName = "";
  @state() private _activeTab: TabId = (getSearchParam("tab") as TabId) || "tags";
  @state() private _selectedCanonical = "";

  createRenderRoot() {
    return this;
  }

  private _tagsTask = new Task(this, {
    args: () => [this.imageName] as const,
    task: ([name]) => fetchApi<types.TagsResponse>(`/v1/images/${name}/tags`),
  });

  private get _canonical(): string {
    return this._selectedCanonical || this._tagsTask.value?.tags[0]?.canonical || "";
  }

  private get _canonicals(): string[] {
    return this._tagsTask.value ? uniqueCanonicals(this._tagsTask.value.tags) : [];
  }

  private _setTab(tab: TabId) {
    this._activeTab = tab;
    setSearchParam("tab", tab);
  }

  private _goBack(e: Event) {
    e.preventDefault();
    navigate("/");
  }

  private _renderTabs() {
    const tags = this._tagsTask.value;
    if (!tags) return "";
    const pullUrl = imageCache.getImage(this.imageName)?.pull_url ?? "";
    const pullCommand = pullUrl ? `podman pull ${pullUrl}` : "";

    return html`
      <div class="mb-6">
        <a href="/" @click=${this._goBack} class="text-gray-500 hover:text-gray-300 text-sm transition-colors">
          &larr; Back to images
        </a>
        <h1 class="text-white text-2xl font-bold mt-2">${this.imageName}</h1>
        ${pullCommand
          ? html`
              <div class="mt-2 flex items-center gap-2">
                <code class="text-gray-400 text-sm bg-hb-bg border border-hb-border rounded px-2 py-1">
                  ${pullCommand}
                </code>
                <copy-button .value=${pullCommand} label="pull command"></copy-button>
              </div>
            `
          : ""}
      </div>

      <div class="flex flex-wrap gap-1 border-b border-hb-border mb-4 pb-2">
        ${ALL_TABS.filter((t) => !t.hidden).map(
          (t) => html`
            <button
              @click=${() => this._setTab(t.id)}
              class="px-3 py-1.5 text-sm rounded-t transition-colors cursor-pointer
                     ${this._activeTab === t.id
                       ? "bg-hb-surface text-hb-accent border border-hb-border border-b-hb-surface -mb-[3px]"
                       : "text-gray-400 hover:text-gray-200"}"
            >
              ${t.label}
            </button>
          `,
        )}
      </div>

      ${this._renderActiveTab(tags)}
    `;
  }

  private _renderActiveTab(tags: types.TagsResponse) {
    switch (this._activeTab) {
      case "tags":
        return html`
          <tags-tab
            .imageName=${this.imageName}
            .tags=${tags.tags}
            .variants=${tags.variants}
            .selectedCanonical=${this._selectedCanonical}
            @canonical-change=${(e: CustomEvent<string>) => (this._selectedCanonical = e.detail)}
            @tab-change=${(e: CustomEvent<string>) => this._setTab(e.detail as TabId)}
          ></tags-tab>
        `;
      case "overview":
        return html`<overview-tab .imageName=${this.imageName}></overview-tab>`;
      case "details":
        return html`
          <details-tab
            .imageName=${this.imageName}
            .canonical=${this._canonical}
            .canonicals=${this._canonicals}
            .tags=${tags.tags}
            @canonical-change=${(e: CustomEvent<string>) => (this._selectedCanonical = e.detail)}
          ></details-tab>
        `;
      case "sbom":
        return html`
          <sbom-tab
            .imageName=${this.imageName}
            .canonical=${this._canonical}
            .canonicals=${this._canonicals}
            .tags=${tags.tags}
            @canonical-change=${(e: CustomEvent<string>) => (this._selectedCanonical = e.detail)}
          ></sbom-tab>
        `;
      case "history":
        return html`
          <history-tab
            .imageName=${this.imageName}
            .canonical=${this._canonical}
            .canonicals=${this._canonicals}
            .tags=${tags.tags}
            @canonical-change=${(e: CustomEvent<string>) => (this._selectedCanonical = e.detail)}
          ></history-tab>
        `;
      case "vulnerabilities":
        return html`
          <vulnerabilities-tab
            .imageName=${this.imageName}
            .canonical=${this._canonical}
            .canonicals=${this._canonicals}
            .tags=${tags.tags}
            @canonical-change=${(e: CustomEvent<string>) => (this._selectedCanonical = e.detail)}
          ></vulnerabilities-tab>
        `;
      case "comparison":
        return html`
          <comparison-tab
            .imageName=${this.imageName}
            .canonical=${this._canonical}
            .canonicals=${this._canonicals}
            .tags=${tags.tags}
            @canonical-change=${(e: CustomEvent<string>) => (this._selectedCanonical = e.detail)}
          ></comparison-tab>`;
      default:
        return "";
    }
  }

  render() {
    return html`
      ${this._tagsTask.render({
        pending: () => html`<loading-spinner></loading-spinner>`,
        error: (e) => html`<error-message .message=${String(e)}></error-message>`,
        complete: () => this._renderTabs(),
      })}
    `;
  }
}
